<template>
    <vx-selectable v-model="model"
        placeholder="Select a collection"
        :reduce="option => option.id"
        label="name"
        :loading="loading"
        :options="selectableOptions"
        :disabled="props.disabled"
        :size="props.size"
        @search="search"
        @option:selected="onSelected"
        data-test="group-collection-selectable">
    </vx-selectable>
</template>

<script setup>
import { VxSelectable } from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { groupCollectionsClient } from '~/services';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import debounce from '~/utils/debounce';

const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();

const model = defineModel();

const emit = defineEmits(['collectionSelected']);

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String,
        default: 'md',
    }
});

const options = ref([]);
const loading = ref();

// vue-select prints the bare id for a value it cannot find among the options, so the saved
// collection is kept as its own option while the list loads and once it no longer exists.
const selected = ref(null);

const selectableOptions = computed(() =>
    null !== selected.value && !options.value.some((option) => option.id === selected.value.id)
        ? [...options.value, selected.value]
        : options.value
);

watch(
    model,
    async (id) => {
        if (null === id || undefined === id) {
            selected.value = null;

            return;
        }

        if (selected.value?.id === id) {
            return;
        }

        selected.value = options.value.find((option) => option.id === id) ?? { id, name: '' };

        if ('' !== selected.value.name) {
            return;
        }

        try {
            const response = await groupCollectionsClient.getGroupCollection({ teamId }, id);
            if (model.value === id) {
                selected.value = response.data;
            }
        } catch (e) {
            if (model.value === id) {
                selected.value = {
                    id,
                    name: HTTP_NOT_FOUND === e?.response?.status ? 'Deleted collection' : 'Unavailable collection',
                };
            }
        }
    },
    { immediate: true }
);

const onSelected = (option) => {
    selected.value = option;
    emit('collectionSelected', option);
};

const search = debounce((query) => {
    loading.value = true;

    groupCollectionsClient.getGroupCollections({ teamId }, {
        query: query || model.value,
    }).then((response) => {
        options.value = response.data.data;
    }).catch((e) => {
        console.error('Failed to load group collections:', e);
        toasted.global.platform_error();
    }).finally(() => {
        loading.value = false;
    })
}, 300);

search();
</script>
